/**
 * SplashScreen.js - Lobby and Splash Screen Management
 *
 * Handles:
 * - Splash screen initialization and events
 * - Lobby UI state management
 * - Ready/Unready button functionality
 * - Player name input
 * - Countdown display
 */

import { ClientLogger } from '../logger/ClientLogger.js';

const splashLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {}, debug: () => {} };
})();
const splashLogger = ClientLogger.getLogger('ui') || splashLoggerFallback;

const SplashScreen = {
  // DOM element references
  elements: {
    splashScreen: null,
    readyButton: null,
    unreadyButton: null,
    nameInput: null,
    lobbyStatus: null,
    lockerButton: null,
    lockerChip: null,
    warmupOverlay: null
  },

  // Mode selector state
  modeState: {
    modes: [],
    currentModeId: null
  },

  // Callbacks for game actions
  callbacks: {
    onReady: null,
    onUnready: null,
    onNameChange: null,
    showNotification: null,
    onLocker: null,
    isLoggedIn: null
  },

  // Game state reference
  gameState: null,

  // The one delegated click listener for the relocated Locker button
  onDocumentClick: null,

  /**
   * Initialize the splash screen
   * @param {Object} options - Configuration options
   * @param {Object} options.gameState - Game state reference
   * @param {Object} options.callbacks - Callback functions
   */
  init(options = {}) {
    this.gameState = options.gameState;
    Object.assign(this.callbacks, options.callbacks || {});

    // Cache DOM elements
    this.elements.splashScreen = document.getElementById('splashScreen');
    this.elements.readyButton = document.getElementById('readyButton');
    this.elements.unreadyButton = document.getElementById('unreadyButton');
    this.elements.nameInput = document.getElementById('playerNameInput');
    this.elements.lobbyStatus = document.getElementById('lobbyStatus');
    this.elements.modeSelector = document.getElementById('modeSelector');
    this.elements.modeButtons = document.getElementById('modeButtons');
    this.elements.modeDescription = document.getElementById('modeDescription');
    this.elements.lockerButton = document.getElementById('lockerButton');
    this.elements.lockerChip = document.getElementById('lockerChip');
    this.elements.warmupOverlay = document.getElementById('warmupOverlay');

    this.setupEventListeners();
  },

  /**
   * Set up event listeners for splash screen elements
   */
  setupEventListeners() {
    const { nameInput, readyButton, unreadyButton } = this.elements;

    if (!nameInput || !readyButton || !unreadyButton) return;

    // Focus on input when page loads
    nameInput.focus();

    // Auto-clear default "Player" text when clicking into the input
    nameInput.addEventListener('focus', () => {
      if (nameInput.value === 'Player') {
        nameInput.value = '';
      }
    });

    // Restore default if left empty
    nameInput.addEventListener('blur', () => {
      if (nameInput.value.trim() === '') {
        nameInput.value = 'Player';
      }
    });

    // Allow Enter key to trigger ready
    nameInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        readyButton.click();
      }
    });

    // Handle ready button click
    readyButton.addEventListener('click', () => this.handleReadyClick());

    // Handle unready button click
    unreadyButton.addEventListener('click', () => this.handleUnreadyClick());

    // Keyboard path to Unready: the OS cursor is hidden for crosshair aiming
    // once spawned, so a warm-up player may not be able to see it to click
    // the on-screen button. Guarded so it never fires accidentally: only
    // while the overlay is actually visible, not while typing in a text
    // field, not on a held-key repeat, not if the event was already handled
    // elsewhere, and not while a modal dialog screen (Locker/Account/
    // Leaderboard, each its own Escape-to-close target) is open on top.
    // Exactly one such listener can ever be attached: a re-init drops the old one.
    if (this.onEscapeKeyDown) document.removeEventListener('keydown', this.onEscapeKeyDown);
    this.onEscapeKeyDown = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented || e.repeat) return;
      if (!this.elements.warmupOverlay || this.elements.warmupOverlay.classList.contains('hidden')) return;
      // The overlay stays up through COUNTDOWN with the button hidden, and the
      // button is disabled while an unready is in flight; Escape must be no
      // more capable than a click on the button itself.
      const { unreadyButton } = this.elements;
      if (!unreadyButton || unreadyButton.disabled || getComputedStyle(unreadyButton).display === 'none') return;

      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) return;

      // Locker/Account/Leaderboard all use the same shell shape for their root
      // (role="dialog" aria-modal="true", toggled via a "hidden" class) — a
      // cheap, decoupled way to ask "is any of them open right now?" without
      // importing those modules directly.
      if (document.querySelector('[role="dialog"][aria-modal="true"]:not(.hidden)')) return;

      this.handleUnreadyClick();
    };
    document.addEventListener('keydown', this.onEscapeKeyDown);

    // Locker (6A): open the cosmetics screen; UIManager decides what "open" means.
    // The button now lives in the header nav and is painted by HeaderNav, possibly
    // after this runs, so delegate from the document rather than a cached node.
    // Exactly one such listener can ever be attached: a re-init drops the old one.
    if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
    this.onDocumentClick = (e) => {
      if (e.target?.closest?.('#lockerButton')) this.callbacks.onLocker?.();
    };
    document.addEventListener('click', this.onDocumentClick);
  },

  /**
   * Handle ready button click
   */
  handleReadyClick() {
    const { readyButton, nameInput } = this.elements;

    // Get player name from input (trim whitespace)
    const trimmedName = nameInput.value.trim();

    // Validate name length (min 3 characters)
    if (trimmedName.length < 3 || (trimmedName === 'Player' && trimmedName.length < 3)) {
      if (this.callbacks.showNotification) {
        this.callbacks.showNotification('Player name must be at least 3 characters!', 'error');
      }
      return;
    }

    // Disable button to prevent double-clicks, and lock the lobby inputs now so a
    // loadout change between this click and the join payload cannot race it (6A D1)
    readyButton.disabled = true;
    this.setLobbyInputsLocked(true);

    // Call the ready callback with the player name
    if (this.callbacks.onReady) {
      this.callbacks.onReady(trimmedName);
    }
  },

  /**
   * Handle unready button click
   */
  handleUnreadyClick() {
    const { unreadyButton } = this.elements;

    unreadyButton.disabled = true;
    unreadyButton.textContent = 'Unreadying...';

    if (this.callbacks.onUnready) {
      this.callbacks.onUnready();
    }
  },

  /**
   * Update ready button state (for joining flow)
   * @param {string} state - 'joining', 'readying', 'ready'
   */
  setReadyButtonState(state) {
    const { readyButton } = this.elements;
    if (!readyButton) return;

    switch (state) {
      case 'joining':
        readyButton.textContent = 'Joining...';
        readyButton.disabled = true;
        break;
      case 'readying':
        readyButton.textContent = 'Readying...';
        readyButton.disabled = true;
        break;
      case 'outdated':
        // Server reported a protocol version mismatch — this client cannot play
        // until it reloads, so there is no point letting them ready up.
        readyButton.textContent = 'Refresh required';
        readyButton.disabled = true;
        this.setLobbyInputsLocked(true);
        break;
      case 'ready':
      default:
        readyButton.textContent = 'Ready';
        readyButton.disabled = false;
        this.setLobbyInputsLocked(false);
        break;
    }
  },

  /**
   * Hide the splash screen (game starting)
   */
  hide() {
    const { splashScreen } = this.elements;
    if (splashScreen) {
      splashScreen.classList.add('hidden');
    }
  },

  /**
   * Show the splash screen (returning to lobby)
   */
  show() {
    const { splashScreen } = this.elements;
    if (splashScreen) {
      splashScreen.classList.remove('hidden');
    }
  },

  /** Show the warm-up overlay (ready status, Unready, countdown) — used
   *  while the room is WAITING/COUNTDOWN, independent of the splash screen. */
  showWarmupOverlay() {
    this.elements.warmupOverlay?.classList.remove('hidden');
  },

  /** Hide the warm-up overlay — the real match has started, or we've
   *  returned to the lobby entirely. */
  hideWarmupOverlay() {
    this.elements.warmupOverlay?.classList.add('hidden');
  },

  /**
   * Update lobby UI based on room state
   * @param {Object} roomData - Room state data
   */
  updateLobbyUI(roomData) {
    const { lobbyStatus, readyButton, unreadyButton } = this.elements;

    if (roomData.state === 'waiting' || roomData.state === 'countdown') {
      this.showWarmupOverlay();
    } else {
      this.hideWarmupOverlay();
    }

    if (!lobbyStatus) return;

    if (roomData.state === 'waiting') {
      // Add pulsing animation and animated dots
      lobbyStatus.className = 'waiting waiting-dots';
      lobbyStatus.style.display = 'block';

      // Show unready button, hide ready button, lock the lobby inputs
      readyButton.style.display = 'none';
      unreadyButton.style.display = 'inline-block';
      // Clear any stuck in-flight state (e.g. an unready the server rejected
      // during a countdown that was then cancelled).
      unreadyButton.disabled = false;
      unreadyButton.textContent = 'Unready';
      this.setLobbyInputsLocked(true);

      // Show ready count
      const readyCount = roomData.readyCount || 0;
      if (readyCount === 0) {
        lobbyStatus.innerHTML = `<span>Waiting for players to ready up...</span>`;
      } else {
        lobbyStatus.innerHTML = `<span>Waiting for player ${readyCount} out of ${roomData.maxPlayers}</span>`;
      }
    } else if (roomData.state === 'countdown') {
      this.setLobbyInputsLocked(true);

      // Remove waiting animation for countdown
      lobbyStatus.className = '';
      lobbyStatus.style.display = 'block';
      lobbyStatus.innerHTML = `<span style="color: #00ff00; font-weight: bold;">Game Starting Soon!</span><br><span style="font-size: 14px;">${roomData.readyCount || roomData.playerCount}/${roomData.maxPlayers} ready</span>`;

      // Hide both buttons during countdown
      readyButton.style.display = 'none';
      unreadyButton.style.display = 'none';
    } else {
      lobbyStatus.style.display = 'none';
      lobbyStatus.className = '';
    }
  },

  /**
   * Show countdown message (called on initial gameStarting event)
   * Uses server-driven ticks for synchronization - no client-side interval
   * @param {number} seconds - Initial countdown value from server
   */
  showCountdownMessage(seconds) {
    let countdownDiv = document.getElementById('countdownMessage');

    if (!countdownDiv) {
      // Create countdown message element
      countdownDiv = document.createElement('div');
      countdownDiv.id = 'countdownMessage';
      countdownDiv.style.cssText = `
        position: absolute;
        top: 40%;
        left: 50%;
        transform: translateX(-50%);
        font-size: 32px;
        font-weight: bold;
        color: #00ff00;
        text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
      `;
      // Fall back to the splash screen if the overlay isn't in this DOM (e.g.
      // an older/partial mount) rather than silently building an orphan div.
      (this.elements.warmupOverlay || this.elements.splashScreen)?.appendChild(countdownDiv);
    }

    // Display initial countdown - server ticks will update this
    this.updateCountdown(seconds);
  },

  /**
   * Update countdown display (called by server-driven countdownTick events)
   * @param {number} seconds - Current countdown value from server
   */
  updateCountdown(seconds) {
    const countdownDiv = document.getElementById('countdownMessage');
    if (!countdownDiv) return;

    if (seconds > 0) {
      countdownDiv.textContent = `Game starting in ${seconds}...`;
    } else {
      countdownDiv.textContent = 'Starting now!';
    }
  },

  /**
   * Hide countdown message
   */
  hideCountdownMessage() {
    const countdownDiv = document.getElementById('countdownMessage');
    if (countdownDiv) {
      countdownDiv.remove();
    }
  },

  /**
   * Reset splash screen to initial state (after returning to lobby)
   */
  reset() {
    const { lobbyStatus, readyButton, unreadyButton } = this.elements;

    // Reset lobby status
    if (lobbyStatus) {
      lobbyStatus.style.display = 'none';
      lobbyStatus.className = '';
    }

    // Reset countdown message if it exists
    this.hideCountdownMessage();
    this.hideWarmupOverlay();

    // Re-enable ready button
    if (readyButton) {
      readyButton.disabled = false;
      readyButton.textContent = 'Ready';
      readyButton.style.display = 'inline-block';
    }

    // Hide unready button
    if (unreadyButton) {
      unreadyButton.style.display = 'none';
      unreadyButton.disabled = false;
      unreadyButton.textContent = 'Unready';
    }

    this.setLobbyInputsLocked(false);

    if (this.elements.modeSelector) {
      this.elements.modeSelector.style.display = this.modeState.modes.length > 0 ? 'flex' : 'none';
    }
  },

  /**
   * Lock or unlock everything a player may change before joining: the name
   * input and the Locker button. One seam, called from every state transition
   * (ready click, waiting/countdown, readyConfirmed/unreadyConfirmed, reset).
   */
  setLobbyInputsLocked(locked) {
    const { nameInput } = this.elements;
    // The Locker button moved to the header nav, which paints it after this
    // module caches ids — resolve it at call time so the lock always lands.
    const lockerButton = document.getElementById('lockerButton') || this.elements.lockerButton;
    if (nameInput) nameInput.disabled = !!locked;
    if (lockerButton) lockerButton.disabled = !!locked;
  },

  /**
   * Reflect the signed-in account's current display name in the lobby name
   * field — the value `handleReadyClick` actually sends when starting a game.
   * Called whenever the account's name changes (rename, or the async
   * auth-status settle at boot), so the join name never lags behind it.
   */
  setPlayerName(name) {
    const { nameInput } = this.elements;
    if (nameInput && typeof name === 'string' && name) nameInput.value = name;
  },

  /** Show the equipped skin's sprite on the Locker button (null hides the chip). */
  setLockerChip(src) {
    const chip = this.elements.lockerChip;
    if (!chip) return;
    if (src) chip.setAttribute('src', src); else chip.removeAttribute('src');
  },


  /**
   * Update the game mode list for the lobby selector
   * @param {Array} modes
   * @param {string} defaultModeId
   */
  setModeList(modes = [], defaultModeId) {
    const selector = this.elements.modeSelector;
    if (!selector) {
      return;
    }

    const normalizedModes = (Array.isArray(modes) ? modes : [])
      .filter((mode) => mode && mode.enabled !== false);
    splashLogger.debug('SplashScreen.setModeList', normalizedModes.map(m => m.id));
    this.modeState.modes = normalizedModes;

    if (normalizedModes.length === 0) {
      selector.style.display = 'none';
      selector.classList.add('hidden');
      this.modeState.currentModeId = null;
      if (this.elements.modeDescription) {
        this.elements.modeDescription.textContent = '';
      }
      if (this.elements.modeButtons) {
        this.elements.modeButtons.innerHTML = '';
      }
      return;
    }

    selector.style.display = 'flex';
    selector.classList.remove('hidden');
    this.renderModeButtons();
    const startId = defaultModeId || normalizedModes[0].id;
    const startMode = normalizedModes.find((m) => m.id === startId);
    if (startMode?.metadata?.ranked && !this._isLoggedIn()) {
      const fallback = normalizedModes.find((m) => !m.metadata?.ranked) || normalizedModes[0];
      this.selectMode(fallback.id);
    } else {
      this.selectMode(startId);
    }
  },

  /**
   * Render the mode buttons based on current list
   */
  renderModeButtons() {
    const container = this.elements.modeButtons;
    if (!container) {
      return;
    }

    container.innerHTML = '';
    for (const mode of this.modeState.modes) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mode-button';
      button.dataset.modeId = mode.id;
      const ranked = Boolean(mode.metadata?.ranked);
      const name = document.createElement('span');
      name.textContent = mode.name;
      button.appendChild(name);
      if (ranked) {
        const badge = document.createElement('span');
        badge.className = 'mode-badge-ranked';
        badge.textContent = 'Ranked';
        button.appendChild(badge);
      }
      const guestBlocked = ranked && !this._isLoggedIn();
      if (guestBlocked) {
        button.disabled = true;
        button.title = 'Sign in to play ranked';
        button.setAttribute('aria-disabled', 'true');
      }
      button.addEventListener('click', () => {
        if (button.disabled) return;
        this.selectMode(mode.id);
      });
      container.appendChild(button);
    }
  },

  /**
   * Select a game mode (updates description and button states)
   * @param {string} modeId
   */
  selectMode(modeId) {
    const mode = this.modeState.modes.find(m => m.id === modeId);
    if (!mode) return;
    if (mode.metadata?.ranked && !this._isLoggedIn()) return;
    this.modeState.currentModeId = mode.id;
    this.highlightSelectedButton(mode.id);
    this.updateModeDescription(mode);
  },

  /**
   * Highlight the active mode button
   * @param {string} selectedId
   */
  highlightSelectedButton(selectedId) {
    const container = this.elements.modeButtons;
    if (!container) return;
    for (const child of container.children) {
      if (child.dataset.modeId === selectedId) {
        child.classList.add('active');
      } else {
        child.classList.remove('active');
      }
    }
  },

  /**
   * Update the mode description text
   * @param {Object} mode
   */
  updateModeDescription(mode) {
    if (!this.elements.modeDescription) return;
    const description = mode.description || mode.name;
    this.elements.modeDescription.textContent = description;
  },

  /**
   * Get the currently selected mode ID
   * @returns {string|null}
   */
  getSelectedModeId() {
    return this.modeState.currentModeId;
  },

  _isLoggedIn() {
    return typeof this.callbacks.isLoggedIn === 'function' ? Boolean(this.callbacks.isLoggedIn()) : false;
  },

  onAuthChanged() {
    if (this.modeState.modes.length === 0) return;
    const current = this.modeState.currentModeId;
    this.renderModeButtons();
    const stillAllowed = this.modeState.modes.find((m) => m.id === current && !(m.metadata?.ranked && !this._isLoggedIn()));
    this.selectMode(stillAllowed ? current : (this.modeState.modes.find((m) => !m.metadata?.ranked) || this.modeState.modes[0]).id);
  }
};

// Export for use in other modules
// For now, SplashScreen is a global object loaded via script tag

export { splashLoggerFallback, splashLogger, SplashScreen };
